<script setup>
import { computed, useAttrs } from 'vue';
import { useBulkActions } from 'dashboard/composables/chatlist/useBulkActions.js';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import BulkAgentActions from './BulkAgentActions.vue';
import BulkUpdateActions from './BulkUpdateActions.vue';
import BulkLabelActions from './BulkLabelActions.vue';
import BulkTeamActions from './BulkTeamActions.vue';

const props = defineProps({
  conversations: {
    type: Array,
    default: () => [],
  },
  allConversationsSelected: {
    type: Boolean,
    default: false,
  },
  selectedInboxes: {
    type: Array,
    default: () => [],
  },
  showOpenAction: {
    type: Boolean,
    default: false,
  },
  showResolvedAction: {
    type: Boolean,
    default: false,
  },
  showSnoozedAction: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['selectAllConversations']);

defineOptions({
  inheritAttrs: false,
});

const attrs = useAttrs();

const {
  resetBulkActions,
  onBulkMarkAsRead,
  onBulkMarkAsUnread,
  onBulkDelete,
  onAssignAgent,
  onAssignLabels,
  onAssignTeamsForBulk: onAssignTeam,
  onUpdateConversations,
} = useBulkActions();

// Computed property with getter/setter to enable v-model usage
const allSelected = computed({
  get: () => props.allConversationsSelected,
  set: value => {
    emit('selectAllConversations', value);
  },
});
</script>

<template>
  <Transition
    enter-active-class="transition-all duration-200 ease-out origin-top"
    enter-from-class="opacity-0 -translate-y-4"
    enter-to-class="opacity-100 translate-y-0"
    leave-active-class="transition-all duration-150 ease-in origin-top"
    leave-from-class="opacity-100 translate-y-0"
    leave-to-class="opacity-0 -translate-y-4"
  >
    <div
      v-if="conversations.length > 0"
      v-bind="attrs"
      class="fixed md:absolute top-[48px] md:top-0 left-0 z-[100] w-full bg-n-surface-1 shadow-sm border-b border-n-weak"
    >
      <div class="flex items-center justify-between p-3">
        <!-- Selection Info & Cancel -->
        <div class="flex items-center gap-3">
          <NextButton
            icon="i-lucide-x"
            ghost
            slate
            class="!p-1.5"
            @click="resetBulkActions"
          />
          <div class="flex items-center gap-2">
            <label class="cursor-pointer flex items-center gap-2 m-0">
              <Checkbox
                v-model="allSelected"
                :indeterminate="!allConversationsSelected"
              />
              <span class="font-medium text-n-slate-12 text-sm">
                {{ conversations.length }} Selected
              </span>
            </label>
          </div>
        </div>

        <!-- Action Buttons & Dropdowns -->
        <div class="flex items-center gap-1 overflow-x-auto no-scrollbar">
          <NextButton
            icon="i-lucide-mail-open"
            v-tooltip.bottom="'Mark as Read'"
            ghost
            slate
            class="!p-2 flex-shrink-0"
            @click="onBulkMarkAsRead"
          />
          <NextButton
            icon="i-lucide-mail"
            v-tooltip.bottom="'Mark as Unread'"
            ghost
            slate
            class="!p-2 flex-shrink-0"
            @click="onBulkMarkAsUnread"
          />
          <NextButton
            icon="i-lucide-trash"
            v-tooltip.bottom="'Delete'"
            ghost
            ruby
            class="!p-2 flex-shrink-0"
            @click="onBulkDelete"
          />
          <div class="w-px h-5 bg-n-weak mx-1 flex-shrink-0" />
          <BulkLabelActions @assign="onAssignLabels" class="flex-shrink-0" />
          <BulkUpdateActions
            :show-resolve="!showResolvedAction"
            :show-reopen="!showOpenAction"
            :show-snooze="!showSnoozedAction"
            @update="onUpdateConversations"
            class="flex-shrink-0"
          />
          <BulkAgentActions
            :selected-inboxes="selectedInboxes"
            :conversation-count="conversations.length"
            @select="onAssignAgent"
            class="flex-shrink-0"
          />
          <BulkTeamActions
            :conversation-count="conversations.length"
            @select="onAssignTeam"
            class="flex-shrink-0"
          />
        </div>
      </div>
    </div>
  </Transition>
</template>
